import { css } from "@emotion/css";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useCallback } from "react";
import * as React from "react";
import { useFocusFirstFocusableElement } from "../../hooks/useFocusTrap";
import { Overlay } from "./Overlay";
export interface DialogBaseProps {
    children?: React.ReactNode;
    open: boolean;
    onClose?: () => void;
    closeOnEscape?: boolean;
    closeOnBackdropClick?: boolean;
    accessibleName?: string;
}
export function DialogBase(props: DialogBaseProps) {
    const { children, open, onClose, closeOnEscape = true, closeOnBackdropClick = true } = props;
    const focusFirstElement = useFocusFirstFocusableElement();
    const latestCloseRef = React.useRef(props.onClose);
    latestCloseRef.current = props.onClose;
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const handleKeyUp = useCallback((event: KeyboardEvent) => {
        if (closeOnEscape && event.key === "Escape" && event.target instanceof HTMLElement) {
            latestCloseRef.current?.();
        }
        else {
            event.preventDefault();
        }
    }, [closeOnEscape]);
    React.useEffect(() => {
        document.addEventListener("keyup", handleKeyUp);
        return () => {
            document.removeEventListener("keyup", handleKeyUp);
        };
    }, [handleKeyUp]);
    React.useEffect(() => {
        if (open && dialogRef.current) {
            focusFirstElement(dialogRef.current);
        }
    }, [focusFirstElement, open]);
    return (<>
            {open && (<Overlay onClick={() => {
                if (closeOnBackdropClick) {
                    onClose?.();
                }
            }}>
                    <div ref={dialogRef} className={styles.dialog} role={"dialog"} aria-label={props.accessibleName} aria-modal={"true"}>
                        {children}
                    </div>
                </Overlay>)}
        </>);
}
const styles = {
    dialog: css({
        border: "none",
        padding: 0,
        position: "fixed",
        margin: "auto",
        maxWidth: "100vw", // without this the browser enforces a small amount of horizontal margin
        maxHeight: "100vh", // without this the browser enforces a small amount of vertical margin
        borderRadius: borderRadius.large,
        background: themeTokens.color.dialog.background.primary,
        "& *": {
            boxSizing: "border-box",
        },
        "::backdrop": {
            background: themeTokens.color.overlay.primary,
        },
    }),
};
DialogBase.displayName = "DialogBase";
